/**
 * Whether the shareholder dashboard shows the Transfer Request / Redemption action cards.
 *
 * A client turns shareholder-initiated requests off with `shareholder_can_request: false` on its
 * own `{client}::transfer-request::transfer-request-v1` / `{client}::redemptions::redemptions-v1`
 * NthWfDefinitions record (absent or true = on). user/submit-transfer-request.py and
 * user/submit-redemption.py enforce the same field server-side: off refuses the shareholder's own
 * request and an observer's, and still accepts an admin initiating on a shareholder's behalf.
 */

import { searchWorkflowDefinitionsAPI } from './api-urls.js';

export const TRANSFER_REQUEST_WORKFLOW = { workflowType: 'transfer-request', workflowName: 'transfer-request-v1' };
export const REDEMPTIONS_WORKFLOW = { workflowType: 'redemptions', workflowName: 'redemptions-v1' };

/**
 * False only when the definition sets `shareholder_can_request` to exactly false.
 *
 * @param {Record<string, any> | null | undefined} definition
 * @returns {boolean}
 */
export function shareholderCanRequest(definition) {
	return definition?.shareholder_can_request !== false;
}

/**
 * Whether to show a request card: the client flag must be on; an admin viewing a shareholder
 * (who submits on their behalf) always sees it; anyone else sees it when the client allows
 * shareholder requests. `loadCanRequest` is only called when its answer matters.
 *
 * @param {{ flagOn: boolean; byAdmin: boolean; loadCanRequest: () => Promise<boolean> }} params
 * @returns {Promise<boolean>}
 */
export async function requestCardShown({ flagOn, byAdmin, loadCanRequest }) {
	if (!flagOn) return false;
	if (byAdmin) return true;
	return loadCanRequest();
}

const _cache = new Map();

/** Test hook: forget cached definition reads. */
export function clearShareholderCanRequestCache() {
	_cache.clear();
}

/**
 * Reads `shareholder_can_request` from the client's own definition record (the record the submit
 * Lambda reads; the `*` record is ignored). Cached per client and workflow for the page's
 * lifetime, so dashboard re-renders don't refetch. Resolves true when the record is missing or the
 * request fails (the server enforces the setting anyway); a failed read isn't cached.
 *
 * @param {{ client: string; userid: string; cookie: string; workflowType: string; workflowName: string; axios?: any }} params
 * @returns {Promise<boolean>}
 */
export function loadShareholderCanRequest({ client, userid, cookie, workflowType, workflowName, axios = globalThis.axios }) {
	const definitionId = `${client}::${workflowType}::${workflowName}`;
	if (_cache.has(definitionId)) return _cache.get(definitionId);
	const pending = (async () => {
		try {
			const res = await axios.get(searchWorkflowDefinitionsAPI, {
				params: { client, userid, cookie, workflow_name: workflowName },
			});
			const items = Array.isArray(res?.data?.items) ? res.data.items : [];
			return shareholderCanRequest(items.find((item) => item?.workflow_definition_id === definitionId));
		} catch {
			_cache.delete(definitionId);
			return true;
		}
	})();
	_cache.set(definitionId, pending);
	return pending;
}
